
import { computed, defineComponent, onMounted, PropType, ref, Ref } from 'vue';
import useCountries from '@/shared/address/useCountries';
import { useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import { B2CCompactNewsletterTileModel } from '@/types/B2CcontentServerContract';
import { Form } from 'vee-validate';
import InputText from '@/shared/form/InputText.vue';
import InputSelect from '@/shared/form/InputSelect.vue';
import { B2CClientService, SubscribeToNewsletterRequest } from '@/b2c/shared/api/server-apis';
import Recaptcha, { RecaptchaComponentInstance } from '@/shared/form/recaptcha/Recaptcha.vue';
import { onBeforeUnmount } from '@vue/runtime-core';

export default defineComponent({
    name: 'B2CCompactNewsletterTileModel',
    components: { InputSelect, InputText, Form, Recaptcha },
    props: {
        content: {
            type: Object as PropType<B2CCompactNewsletterTileModel>,
            required: true,
        },
    },
    setup(props) {
        const { countries, siteCountryCode } = useCountries();
        const labels = useBlockLabels();
        const storeList = computed(() => props.content.stores);
        const showCountrySelector = computed(() => countries.value.length > 1 && storeList.value.length === 0);
        const wrapperRef: Ref<HTMLElement | null> = ref(null);
        const currentSignUpStep: Ref<1 | 2 | 3> = ref(1);
        const initialWrapperHeight: Ref<string> = ref('0px');
        const recaptcha: Ref<RecaptchaComponentInstance | null> = ref(null);
        const recaptchaKey: Ref<string> = ref('');
        const newsletterRequest: SubscribeToNewsletterRequest = {
            name: '',
            email: '',
            storeId: '',
            storeName: '',
            countryCode: siteCountryCode.value,
            postalCode: '',
            recaptchaResponse: '',
        };

        onMounted(() => {
            setWrapperHeight();
            window.addEventListener('resize', setWrapperHeight);
        });

        onBeforeUnmount(() => {
            window.removeEventListener('resize', setWrapperHeight);
        });

        const setWrapperHeight = () => {
            if (currentSignUpStep.value === 1 && wrapperRef) {
                const clientRect = wrapperRef.value?.getBoundingClientRect();
                initialWrapperHeight.value = clientRect ? clientRect.height + 'px' : '0px';
            }
        };

        const verifyRecaptcha = () => {
            recaptcha.value && recaptcha.value.reset();
            recaptcha.value && recaptcha.value.execute();
        };

        const submitSubscription = async(formData: signUpFormData): Promise<void> => {
            setStore(formData.storeId);
            newsletterRequest.email = formData.email ?? newsletterRequest.email;
            newsletterRequest.name = formData.name ?? newsletterRequest.name;
            newsletterRequest.countryCode = formData.countryCode ?? siteCountryCode.value;
            newsletterRequest.recaptchaResponse = recaptchaKey.value;
            currentSignUpStep.value++;
            await B2CClientService.subscribeToNewsletter(newsletterRequest);
        };

        function setStore(storeId: string | undefined) {
            const store = storeList.value.find((store) => store.id === storeId);
            if (store) {
                newsletterRequest.storeId = store.id;
                newsletterRequest.storeName = store.description;
            }
        }

        interface signUpFormData {
            email?: string,
            name?: string,
            storeId?: string,
            countryCode?: string,
        }

        return {
            currentSignUpStep,
            countries,
            showCountrySelector,
            initialWrapperHeight,
            labels,
            storeList,
            wrapperRef,
            recaptcha,
            recaptchaKey,
            verifyRecaptcha,
            submitSubscription,
        };
    },
});
